import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface PageChangeInfo {
  pageId: string;
  hasChanges: boolean;
  changeCount: number;
  lastModified: Date | null;
}

interface ChangeDetectionContextType {
  /** Map of page IDs to their change status */
  pageChanges: Map<string, PageChangeInfo>;
  /** Whether any page has unsaved changes */
  hasAnyChanges: boolean;
  /** Total number of changes across all pages */
  totalChangeCount: number;
  /** Register a page's change status */
  registerPageChanges: (pageId: string, hasChanges: boolean, changeCount: number) => void;
  /** Clear changes for a specific page */
  clearPageChanges: (pageId: string) => void;
  /** Clear all changes (e.g., after successful submission) */
  clearAllChanges: () => void;
  /** Get change info for a specific page */
  getPageChangeInfo: (pageId: string) => PageChangeInfo | undefined;
  /** Get list of pages with changes */
  getPagesWithChanges: () => string[];
  /** Mark all changes as sent for review */
  markAsSentForReview: () => void;
  /** Whether changes have been sent for review */
  sentForReview: boolean;
}

const ChangeDetectionContext = createContext<ChangeDetectionContextType | undefined>(undefined);

interface ChangeDetectionProviderProps {
  children: ReactNode;
}

export const ChangeDetectionProvider: React.FC<ChangeDetectionProviderProps> = ({ children }) => {
  const [pageChanges, setPageChanges] = useState<Map<string, PageChangeInfo>>(new Map());
  const [sentForReview, setSentForReview] = useState(false);

  const registerPageChanges = useCallback((pageId: string, hasChanges: boolean, changeCount: number) => {
    setPageChanges(prev => {
      const newMap = new Map(prev);
      newMap.set(pageId, {
        pageId,
        hasChanges,
        changeCount,
        lastModified: hasChanges ? new Date() : prev.get(pageId)?.lastModified || null,
      });
      return newMap;
    });
    // If new changes are registered, reset the sent for review flag
    if (hasChanges && changeCount > 0) {
      setSentForReview(false);
    }
  }, []);

  const clearPageChanges = useCallback((pageId: string) => {
    setPageChanges(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(pageId);
      if (existing) {
        newMap.set(pageId, {
          ...existing,
          hasChanges: false,
          changeCount: 0,
        });
      }
      return newMap;
    });
  }, []);

  const clearAllChanges = useCallback(() => {
    setPageChanges(prev => {
      const newMap = new Map();
      prev.forEach((value, key) => {
        newMap.set(key, {
          ...value,
          hasChanges: false,
          changeCount: 0,
        });
      });
      return newMap;
    });
    // Don't reset sentForReview here - it's managed separately
  }, []);

  const getPageChangeInfo = useCallback((pageId: string): PageChangeInfo | undefined => {
    return pageChanges.get(pageId);
  }, [pageChanges]);

  const getPagesWithChanges = useCallback((): string[] => {
    const pagesWithChanges: string[] = [];
    pageChanges.forEach((info, pageId) => {
      if (info.hasChanges) {
        pagesWithChanges.push(pageId);
      }
    });
    return pagesWithChanges;
  }, [pageChanges]);

  const markAsSentForReview = useCallback(() => {
    setSentForReview(true);
    // Clear all page changes after sending for review
    clearAllChanges();
  }, [clearAllChanges]);

  // Calculate derived values
  const hasAnyChanges = Array.from(pageChanges.values()).some(info => info.hasChanges);
  const totalChangeCount = Array.from(pageChanges.values()).reduce((sum, info) => sum + info.changeCount, 0);

  const value: ChangeDetectionContextType = {
    pageChanges,
    hasAnyChanges,
    totalChangeCount,
    registerPageChanges,
    clearPageChanges,
    clearAllChanges,
    getPageChangeInfo,
    getPagesWithChanges,
    markAsSentForReview,
    sentForReview,
  };

  return (
    <ChangeDetectionContext.Provider value={value}>
      {children}
    </ChangeDetectionContext.Provider>
  );
};

export const useChangeDetectionContext = (): ChangeDetectionContextType => {
  const context = useContext(ChangeDetectionContext);
  if (!context) {
    throw new Error('useChangeDetectionContext must be used within a ChangeDetectionProvider');
  }
  return context;
};

export default ChangeDetectionContext;
